import { Avatar, Box, CardContent, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'
import { formatCurrency } from 'components/DataGrid/operators/formatNumbers'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { OrganizationTableItem, ItemTypes } from 'types'
import { stringAvatar } from 'components/DataGrid/gridOperators'
dayjs.extend(utc)

export const StyledCardContent = styled(CardContent)(({ theme }) => ({
  backgroundColor: theme.palette.background.section,
  overflowX: 'hidden',
  p: 1.5,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
}))

export const RenderOrgImage = ({ org }: { org: OrganizationTableItem }) => {
  const theme = useTheme()
  return (
    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
      {org?.photoUrl ? (
        <span
          style={{
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            borderRadius: 4, // optional
            verticalAlign: 'middle',
            marginLeft: 4,
            marginRight: 4,
          }}
        >
          <img
            src={org?.photoUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
            decoding="async"
          />
        </span>
      ) : (
        <Avatar
          {...stringAvatar(org?.name, { fontSize: 12 })}
          alt={org?.name}
          sx={{
            bgcolor: theme.palette.background.default,
            alignContent: 'center',
            height: 28,
            width: 28,
            objectFit: 'cover',
          }}
        />
      )}
    </span>
  )
}
export const RenderPrimary = ({ organizations, item }: { organizations: OrganizationTableItem[]; item: ItemTypes }) => {
  const theme = useTheme()
  const org = organizations?.find(o => o?.PK === item?.PK)

  return (
    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
      {org && <RenderOrgImage org={org} />} • <span> {item?.name ?? '—'}</span>
    </span>
  )
}
export const SpanWithColor = ({ identifier, children }: { identifier: string; children: React.ReactNode }) => (
  <span style={{ color: renderColorByString(identifier) }}>{children}</span>
)
export const SpanCurrency = ({ children }: { children: React.ReactNode }) => (
  <span style={{ color: 'rgb(80, 200, 120)' }}>{children}</span>
)
export const RenderStatus = ({ item, tag }: { item: ItemTypes; tag?: React.ReactNode }) => (
  <span>
    Status: <SpanWithColor identifier={item?.status}>{item?.status ?? '—'}</SpanWithColor> •{' '}
    {item?.startDate ? dayjs(item?.startDate).format('MMM D') : '—'} →{' '}
    {item?.endDate ? dayjs(item?.endDate).format('MMM D, YYYY') : '—'} {tag}
  </span>
)
export const RenderType = ({ item, tag }: { item: ItemTypes; tag?: React.ReactNode }) => (
  <span>
    {/* Type: <span style={{ color: renderColorByString(item?.type) }}>{item?.type ?? '—'}</span> • Status:{' '} */}
    <SpanWithColor identifier={item?.type}>{item?.type ?? '—'}</SpanWithColor> •{' '}
    <SpanWithColor identifier={item?.dealStatus}>{item?.dealStatus ?? '—'}</SpanWithColor> •{' '}
    <SpanWithColor identifier={item?.status}>{item?.status ?? '—'}</SpanWithColor> • MRR:{' '}
    <SpanCurrency>{formatCurrency(item?.monthlyTotal)}</SpanCurrency> {tag}
  </span>
)
export const RenderOrgStats = ({ item, status }: { item: ItemTypes; status: string }) => {
  return (
    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
      Deals: {item.dealCount} • Total: <SpanCurrency>{formatCurrency(item.contractTotal)}</SpanCurrency> • MRR:{' '}
      <SpanCurrency>{formatCurrency(item.monthlyTotal)}</SpanCurrency> • Status:{' '}
      <SpanWithColor identifier={status}>{status ?? '—'}</SpanWithColor>
    </span>
  )
}
export const RenderCurrencyPrimary = ({
  organizations,
  item,
}: {
  organizations: OrganizationTableItem[]
  item: ItemTypes
  value?: number
}) => {
  const org = organizations?.find(o => o?.PK === item?.PK) ?? null

  return (
    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
      {org && <RenderOrgImage org={org} />} • <span>{item?.productName ?? '—'}</span> •{' '}
      <SpanCurrency>{item?.price ? formatCurrency(item?.price) : `$0.00`}</SpanCurrency>
    </span>
  )
}
export const RenderTaskPrimary = ({ item, org }: { item: ItemTypes; org?: OrganizationTableItem }) => {
  const month =
    new Intl.DateTimeFormat('en', { month: 'long', timeZone: 'UTC' }).format(
      new Date(item?.timeCreated ?? item?.creation_timestamp ?? null)
    ) ?? null
  return (
    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
      {org && <RenderOrgImage org={org} />} • <span> {item?.name ?? '—'}</span> •{' '}
      {item?.SK?.startsWith('MONTHLY_TASK_') && month && <span style={{ fontSize: 11 }}>{month}</span>}
      {item?.SK?.startsWith('SUBTASK_') && (
        <span style={{ color: renderColorByString('subtask'), fontSize: 11 }}>{'Subtask'}</span>
      )}
      {item?.SK?.startsWith('TASK_') && item?.isCreative && (
        <span style={{ color: renderColorByString('Creative Tasks'), fontSize: 11 }}>{'Creative'}</span>
      )}
      {item?.SK?.startsWith('TASK_') && item?.type === 'website' && (
        <span style={{ color: renderColorByString('Website'), fontSize: 11 }}>{'Website'}</span>
      )}
      {item?.SK?.startsWith('TASK_') && item?.isMarketing && (
        <span style={{ color: renderColorByString('Digital'), fontSize: 11 }}>{'Digital'}</span>
      )}
      {/* <span>
      {image && role && (
        <Avatar
          src={image}
          sx={{
            border: `3px solid ${renderColorByString(role)} !important`,
            height: 24,
            width: 24,
          }}
        />
      )}
    </span> */}
    </span>
  )
}
export const RenderTaskInfo = ({ item, tag }: { item: ItemTypes; tag?: React.ReactNode }) => {
  const dueDate = item?.dueDate ?? item?.dateDue ?? item?.endDate ?? null

  return (
    <span>
      <SpanWithColor identifier={item?.type}>{formatCamelCase(item?.type) ?? '—'}</SpanWithColor> •{' '}
      <SpanWithColor identifier={item?.masterStatus}>{item?.masterStatus ?? '—'}</SpanWithColor> •{' '}
      {item?.startDate ? dayjs(item?.startDate).format('MMM D') : '?'} →{' '}
      {dueDate ? dayjs(dueDate).format('MMM D, YYYY') : '?'} {tag}
    </span>
  )
}
// export const RenderContactInfo = ({ item }: { item: ItemTypes }) => (
//   <span>
//     Deals: {item.dealCount} • Total: <SpanCurrency>{formatCurrency(item.contractTotal)}</SpanCurrency> • MRR:{' '}
//     <SpanCurrency>{formatCurrency(item.monthlyTotal)}</SpanCurrency> • Status:{' '}
//     <SpanWithColor identifier={status}>{status ?? '—'}</SpanWithColor>
//   </span>
// )
export const RenderTitle = ({ title, count }: { title: string; count: number }) => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="body2" sx={{ opacity: 0.6, whiteSpace: 'nowrap' }}>
        ({count ?? 0})
      </Typography>
    </Box>
  )
}
